import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/reducers";
import { AppDispatch } from "../../store/store";

import TransactionsDialog from "./TransactionsDialog";
import { useQuery } from "@tanstack/react-query";
import {
  fetchClientRepeats,
  getTransactionsByEntityId,
} from "../../store/modules/repeatsAndReferrers/repeatsAndReferrersActions";
import theme from "../../styles/theme";

interface IProps {
  isPro: boolean;
  coAgentIds: number[];
}
const ClientRepeatsTab: React.FC<IProps> = ({ isPro, coAgentIds }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { data: clientRepeats, loading } = useSelector(
    (state: RootState) => state.repeatsAndReferrers.clientRepeats,
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<{
    id: number;
    name: string;
  } | null>(null);

  useQuery({
    queryKey: ["fetchClientRepeats", coAgentIds],
    queryFn: () => {
      dispatch(fetchClientRepeats(coAgentIds));

      return Promise.resolve(null);
    },
    refetchOnWindowFocus: false,
  });

  const { data: selectedTransactions = [], isLoading: transactionsLoading } =
    useQuery({
      queryKey: ["clientRepeatTransactions", selectedClient?.id, coAgentIds],
      queryFn: async () => {
        if (!selectedClient?.id) return [];

        const action = getTransactionsByEntityId(selectedClient.id, 1);
        const transactions = await action();

        return transactions;
      },
      enabled: !!selectedClient?.id,
      refetchOnWindowFocus: false,
    });

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedClient(null);
  };
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Client Name",
      minWidth: 200,
      sortable: true,
      filterable: false,

      sortingOrder: ["asc", "desc"],
      flex: 2,
    },
    {
      field: "count",
      headerName: "Repeats",
      minWidth: isMobile ? 100 : 150,
      sortable: true,
      filterable: false,

      sortingOrder: ["desc", "asc"],
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          color="primary"
          variant="outlined"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedClient({ id: params.row.id, name: params.row.name });
            setDialogOpen(true);
          }}
          sx={{
            cursor: "pointer",
          }}
        />
      ),
    },
    {
      field: "totalVolume",
      headerName: "Total Volume",
      minWidth: 120,
      flex: 1,
      type: "number",
      filterable: false,

      sortable: true,
      sortingOrder: ["asc", "desc"],
      valueFormatter: (params: number) => {
        if (params === null || params === undefined) return "";
        return `$${params.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}`;
      },
    },
  ];
  columns.push({
    field: "averagePrice",
    headerName: "Average Price",
    minWidth: 120,
    flex: 1,
    type: "number",
    sortable: true,
    filterable: false,
    sortingOrder: ["asc", "desc"],
    valueFormatter: (params: number) => {
      if (params === null || params === undefined) return "";
      return `$${params.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })}`;
    },
  });

  columns.push({
    field: "totalGci",
    headerName: "Total GCI",
    minWidth: 120,
    flex: 1,
    type: "number",
    sortable: true,
    filterable: false,
    sortingOrder: ["asc", "desc"],
    valueFormatter: (params: number) => {
      if (params === null || params === undefined) return "";
      return `$${params.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })}`;
    },
  });
  columns.push({
    field: "totalNetIncome",
    headerName: "Total Net Income",
    minWidth: 140,
    flex: 1,
    type: "number",
    sortable: true,
    filterable: false,
    sortingOrder: ["asc", "desc"],
    valueFormatter: (params: number) => {
      if (params === null || params === undefined) return "";
      return `$${params.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })}`;
    },
  });

  return (
    <Box sx={{ p: 2 }}>
      <Paper elevation={0} sx={{ p: 2, mb: 2 }}>
        {/* <Typography variant="subtitle1" gutterBottom>
          Clients who appear in multiple transactions
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Click on the transaction count to see the client's transactions.
        </Typography> */}

        <Box sx={{ height: "100%", width: "100%" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : clientRepeats.length === 0 ? (
            <Typography variant="body1" align="center" sx={{ py: 4 }}>
              No client repeats found.
            </Typography>
          ) : (
            <DataGrid
              rows={clientRepeats}
              columns={columns}
              autoHeight
              onRowClick={(params) => {
                setSelectedClient({
                  id: params.row.id,
                  name: params.row.name,
                });
                setDialogOpen(true);
              }}
              density={isMobile ? "compact" : "standard"}
              disableColumnSelector
              disableColumnMenu={isMobile}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 20 },
                },
                sorting: {
                  sortModel: [{ field: "count", sort: "desc" }],
                },
              }}
            />
          )}
        </Box>
      </Paper>

      {selectedClient && (
        <TransactionsDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          transactions={selectedTransactions}
          title={`Transactions for ${selectedClient.name}`}
          loading={transactionsLoading}
        />
      )}
    </Box>
  );
};

export default ClientRepeatsTab;
